import { useMemo } from "react";
import { Sun, Sunrise, Sunset } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatTemp } from "@/lib/format";

/**
 * Daylight arc — semicircular SVG showing sunrise → solar noon → sunset
 * with a small sun marker at the current time of day. Inspired by Apple's
 * Weather sun-position widget. Falls back gracefully when sunrise/sunset
 * are missing.
 *
 * Props:
 *   sunrise / sunset : Unix UTC seconds (from OpenWeather)
 *   tzOffset         : seconds (location's offset from UTC; from OpenWeather `city.timezone`)
 *   temp_max_f, temp_min_f : today's high/low for the inner labels
 */
export default function DaylightArc({ sunrise, sunset, tzOffset = 0, temp_max_f, temp_min_f }) {
  const { user: prefs } = useAuth();
  const unit = prefs?.temperature_unit || "F";
  // Compute the daylight progress using local-time-at-the-location.
  // Strategy: shift every Unix timestamp by tzOffset so that local-midnight
  // sits at the same numeric anchor. Then progress = (now-sunrise)/(sunset-sunrise).
  const { pct, sunriseTxt, sunsetTxt, durationTxt, isDay } = useMemo(() => {
    if (!sunrise || !sunset) {
      return { pct: null, sunriseTxt: "—", sunsetTxt: "—", durationTxt: "", isDay: false };
    }
    const nowSec = Math.floor(Date.now() / 1000);
    const local = (ts) => new Date((ts + tzOffset) * 1000);
    const fmt = (ts) => {
      const d = local(ts);
      const h = d.getUTCHours();
      const m = d.getUTCMinutes();
      const period = h >= 12 ? "PM" : "AM";
      const hh12 = ((h + 11) % 12) + 1;
      return `${hh12}:${String(m).padStart(2, "0")} ${period}`;
    };
    const day = nowSec >= sunrise && nowSec <= sunset;
    let p = (nowSec - sunrise) / (sunset - sunrise);
    if (p < 0) p = 0;
    if (p > 1) p = 1;
    const durMin = Math.round((sunset - sunrise) / 60);
    const hours = Math.floor(durMin / 60);
    const mins = durMin % 60;
    return {
      pct: p,
      isDay: day,
      sunriseTxt: fmt(sunrise),
      sunsetTxt: fmt(sunset),
      durationTxt: `${hours}h ${mins}m of daylight`,
    };
  }, [sunrise, sunset, tzOffset]);

  // SVG geometry — semicircle (180° → 0°). We use a 200x110 viewbox with the
  // arc centred at (100, 100) and radius 90.
  const W = 200, H = 110, CX = 100, CY = 100, R = 90;
  // Endpoint of arc at fraction t (0 = sunrise on the left, 1 = sunset on the right)
  const pointAt = (t) => {
    const theta = Math.PI - Math.PI * t; // 180° → 0°
    return [CX + R * Math.cos(theta), CY - R * Math.sin(theta)];
  };
  const [sx, sy] = pct == null ? [null, null] : pointAt(pct);
  const [ex, ey] = pointAt(pct ?? 1);

  // Daylight track path — semicircle from (CX-R, CY) to (CX+R, CY)
  const trackPath = `M ${CX - R} ${CY} A ${R} ${R} 0 0 1 ${CX + R} ${CY}`;

  // Filled arc up to the sun's current position
  const fillPath = pct == null
    ? null
    : `M ${CX - R} ${CY} A ${R} ${R} 0 0 1 ${sx} ${sy}`;

  return (
    <div className="bg-gradient-to-br from-amber-50 via-white to-sky-50 border border-stone-200 rounded-2xl p-5" data-testid="daylight-arc">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-[0.18em] text-stone-600 font-semibold">Today's sky</div>
        <div className="text-[10px] uppercase tracking-wider text-stone-400">{durationTxt}</div>
      </div>

      <div className="relative">
        <svg viewBox={`0 0 ${W} ${H + 30}`} className="w-full" aria-hidden>
          <defs>
            <linearGradient id="arcGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%"  stopColor="#fcd34d" />
              <stop offset="50%" stopColor="#fbbf24" />
              <stop offset="100%" stopColor="#f97316" />
            </linearGradient>
          </defs>
          {/* Horizon line */}
          <line x1="0" y1={CY} x2={W} y2={CY} stroke="#e7e5e4" strokeWidth="1" strokeDasharray="3 3" />
          {/* Full daylight track (faint) */}
          <path d={trackPath} stroke="#fde68a" strokeWidth="3" fill="none" strokeLinecap="round" />
          {/* Filled arc up to the sun */}
          {fillPath && (
            <path d={fillPath} stroke="url(#arcGrad)" strokeWidth="4" fill="none" strokeLinecap="round" />
          )}
          {/* Sun marker */}
          {pct != null && (
            <g>
              <circle cx={ex} cy={ey} r="11" fill="#fff" stroke="#f59e0b" strokeWidth="2" />
              <circle cx={ex} cy={ey} r="5" fill={isDay ? "#facc15" : "#94a3b8"} />
            </g>
          )}
          {/* Sunrise & sunset labels at the horizon endpoints */}
          <text x={CX - R} y={CY + 18} textAnchor="start" className="fill-stone-500" style={{ fontSize: "9px", fontWeight: 600, letterSpacing: "0.06em" }}>
            SUNRISE
          </text>
          <text x={CX - R} y={CY + 30} textAnchor="start" className="fill-stone-800" style={{ fontSize: "11px", fontWeight: 600 }}>
            {sunriseTxt}
          </text>
          <text x={CX + R} y={CY + 18} textAnchor="end" className="fill-stone-500" style={{ fontSize: "9px", fontWeight: 600, letterSpacing: "0.06em" }}>
            SUNSET
          </text>
          <text x={CX + R} y={CY + 30} textAnchor="end" className="fill-stone-800" style={{ fontSize: "11px", fontWeight: 600 }}>
            {sunsetTxt}
          </text>
        </svg>
      </div>

      {(temp_max_f != null || temp_min_f != null) && (
        <div className="grid grid-cols-2 gap-3 mt-3 text-center">
          <div className="bg-white/70 rounded-xl py-2 border border-stone-100">
            <div className="text-[10px] uppercase tracking-wider text-stone-500">High</div>
            <div className="font-display text-xl font-bold tabular-nums text-stone-900">{formatTemp(temp_max_f, prefs, { noUnit: true })}°<span className="text-xs font-normal text-stone-500">{unit}</span></div>
          </div>
          <div className="bg-white/70 rounded-xl py-2 border border-stone-100">
            <div className="text-[10px] uppercase tracking-wider text-stone-500">Low</div>
            <div className="font-display text-xl font-bold tabular-nums text-stone-900">{formatTemp(temp_min_f, prefs, { noUnit: true })}°<span className="text-xs font-normal text-stone-500">{unit}</span></div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mt-3 text-xs text-stone-500">
        <span className="inline-flex items-center gap-1"><Sunrise className="w-3 h-3 text-amber-600" /> First flight window</span>
        <span className="inline-flex items-center gap-1"><Sun className="w-3 h-3 text-amber-500" /> Peak forage</span>
        <span className="inline-flex items-center gap-1"><Sunset className="w-3 h-3 text-orange-600" /> Workers home</span>
      </div>
    </div>
  );
}
